
import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  CheckCircle2, 
  AlertCircle, 
  User, 
  CreditCard,
  X
} from 'lucide-react';
import { 
  Popover, 
  PopoverTrigger, 
  PopoverContent 
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  icon: React.ReactNode;
}

interface TaskNotificationProps {
  tasks: Task[];
  onTaskComplete: (taskId: string) => void;
}

const TaskNotification: React.FC<TaskNotificationProps> = ({ 
  tasks, 
  onTaskComplete 
}) => {
  const [open, setOpen] = useState(false);
  const [blinking, setBlinking] = useState(true);
  
  // Count incomplete tasks
  const incompleteTasks = tasks.filter(task => !task.completed);
  const hasIncompleteTasks = incompleteTasks.length > 0;
  
  // Blink animation effect
  useEffect(() => {
    if (!hasIncompleteTasks) {
      setBlinking(false);
      return;
    }
    
    const blinkInterval = setInterval(() => {
      setBlinking(prev => !prev);
    }, 1000);
    
    return () => clearInterval(blinkInterval);
  }, [hasIncompleteTasks]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
          <Bell className="h-5 w-5 text-gray-600" />
          {hasIncompleteTasks && (
            <Badge 
              variant="destructive"
              className={cn(
                "absolute -top-1 -right-1 h-3 w-3 p-0 rounded-full", 
                blinking ? "opacity-100" : "opacity-50",
                "transition-opacity duration-300"
              )}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0">
        <div className="bg-blue-50 p-3 rounded-t-md border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-primary-blue">Tasks to Complete</h3>
            <span className="text-xs text-gray-500">
              {tasks.filter(t => t.completed).length}/{tasks.length} completed
            </span>
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {tasks.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No tasks available
            </div>
          ) : (
            <ul className="divide-y">
              {tasks.map((task) => (
                <li key={task.id} className="p-3 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "flex-shrink-0 mt-1",
                      task.completed ? "text-green-500" : "text-gray-400"
                    )}>
                      {task.icon}
                    </div>
                    <div className="flex-1">
                      <p className={cn(
                        "font-medium",
                        task.completed && "line-through text-gray-400"
                      )}>
                        {task.title}
                      </p>
                      <p className="text-sm text-gray-500">{task.description}</p>
                    </div>
                    <div>
                      {task.completed ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Complete
                        </span>
                      ) : (
                        <button
                          onClick={() => onTaskComplete(task.id)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TaskNotification;
